import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { useContact } from "../ContactProvider";

export default function BriefCta() {
  const ref = useRef(null);
  const { open } = useContact();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yGlow = useTransform(scrollYProgress, [0, 1], ["-30%", "30%"]);
  const scaleType = useTransform(scrollYProgress, [0, 0.5, 1], [0.96, 1, 1.02]);

  return (
    <section
      ref={ref}
      data-testid="brief-cta-section"
      className="relative py-20 md:py-32 border-t border-[#2E2E38] overflow-hidden"
    >
      {/* Animated glow that travels through the band as the user scrolls */}
      <motion.div
        aria-hidden
        style={{ y: yGlow }}
        className="pointer-events-none absolute -inset-x-40 inset-y-0 opacity-60"
      >
        <div
          className="absolute inset-0 blur-3xl"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 30% 50%, rgba(227,255,52,0.18) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 80% 60%, rgba(124,58,237,0.22) 0%, transparent 65%)",
          }}
        />
      </motion.div>

      {/* Animated dashed border frame */}
      <svg
        aria-hidden
        className="pointer-events-none absolute inset-6 md:inset-10 w-[calc(100%-48px)] md:w-[calc(100%-80px)] h-[calc(100%-48px)] md:h-[calc(100%-80px)]"
        preserveAspectRatio="none"
      >
        <rect
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          fill="none"
          stroke="#E3FF34"
          strokeWidth="1"
          strokeDasharray="6 10"
          className="tech-border-dash"
        />
      </svg>

      <div className="relative max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.3em] text-[#E3FF34] mb-6 flex items-center gap-3">
            <span className="w-1.5 h-1.5 bg-[#E3FF34] inline-block pulse-halo" />
            Have a brief?
          </p>
        </motion.div>

        <motion.h2
          style={{ scale: scaleType }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          data-testid="brief-cta-heading"
          className="font-['Anton'] uppercase leading-[0.9] tracking-tight text-[#F2F2F2] text-[12vw] md:text-[8vw] lg:text-[6.5vw] origin-left"
        >
          Tell us where you want
          <br />
          to be in{" "}
          <span className="text-[#E3FF34] glitch-hover inline-block">12 months.</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        >
          <button
            type="button"
            onClick={() => open()}
            data-testid="brief-cta-button"
            className="btn-shimmer relative group inline-flex items-center justify-center gap-3 bg-[#E3FF34] text-[#050505] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-sm px-10 py-5 border border-[#E3FF34] hover:bg-transparent hover:text-[#E3FF34] transition-all duration-300 shadow-[0_0_40px_-8px_rgba(227,255,52,0.5)] hover:shadow-[0_0_60px_-6px_rgba(227,255,52,0.7)]"
          >
            <span className="relative z-10 inline-flex items-center gap-3">
              Start a project
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
          </button>

          <div className="flex items-center gap-5 font-['JetBrains_Mono'] text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#7C3AED] inline-block" />
              Reply within 1 business day
            </span>
            <span className="hidden md:inline">·</span>
            <span className="hidden md:inline">No-pressure scoping call</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
